import type { FC } from 'react'
import { Text, Tag, Flex, GridItem, Grid, Skeleton } from '@chakra-ui/react'
import type { ChakraProps } from '@chakra-ui/react'
import { ImgProxy, PLACEHOLDER_SRC, SIZE_410 } from '@yta/image-proxy'
import type { LocationResult } from '@alexanderathoodly/data-models'
import { displayAddress } from '@alexanderathoodly/data-models'
import {
	PinIcn,
	ContentSection,
	ContentSectionHeading,
} from '@alexanderathoodly/ui-library'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { getProviderQuery } from './queries'
import {
	getLocationByUrlQuery,
	similarLocationsQuery,
} from '~/queries/coworking_location'
import { sendGA4Event } from '~/analytics/ga'
import { useTranslation } from 'react-i18next'

interface SimilarLocationCardProps {
	location: LocationResult
}

const SimilarLocationCard: FC<SimilarLocationCardProps & ChakraProps> = ({
	location,
	...rest
}) => {
	const { t } = useTranslation()
	const pathBase = location.is_flex_office
		? t('/kontor', { ns: 'url' })
		: t('/kontorshotell', { ns: 'url' })
	return (
		<GridItem
			as={Link}
			to={`${pathBase}/${location.url}`}
			role="group"
			onClick={() => {
				sendGA4Event('site_interaction', {
					type: 'navigation',
					sub_type: 'related_listings',
				})
			}}
			{...rest}
		>
			<ImgProxy
				src={location.images[0] || PLACEHOLDER_SRC}
				sizeSet={{ base: SIZE_410 }}
				width="100%"
				loading="lazy"
				alt={t('kontorshotell i {{city}} - {{location_name}}', {
					ns: 'meta_data',
					city: location.address.city,
					location_name: location.name,
				})}
			/>
			<Text
				fontSize="md"
				color="blue.500"
				mt={4}
				_groupHover={{ textDecoration: 'underline' }}
			>
				{location.name}
			</Text>
			<Flex alignItems="center" mt={1}>
				<PinIcn
					h={4}
					w={4}
					outer={{ fill: 'blue.500', stroke: 'blue.500' }}
					inner={{ fill: 'white' }}
					mr={1}
				/>
				<Text fontSize="sm" color="gray.500">
					{displayAddress(location.address)}
				</Text>
			</Flex>
			{location.highlights.map((highlight) => (
				<Tag
					key={highlight}
					borderRadius="full"
					color="light-blue"
					fontSize="xs"
					mr={1}
					mt={2}
				>
					{t(highlight)}
				</Tag>
			))}
		</GridItem>
	)
}

const SimilarLocationsSection: FC<{ url: string }> = ({ url }) => {
	const { t } = useTranslation()
	const locationResult = useQuery(getLocationByUrlQuery(url, useQueryClient()))
	const providerResult = useQuery(
		getProviderQuery(locationResult.data?.provider)
	)
	const similarLocationsResult = useQuery(
		similarLocationsQuery(locationResult.data?.id)
	)

	if (
		locationResult.isLoading ||
		providerResult.isLoading ||
		similarLocationsResult.isLoading
	) {
		return <Skeleton />
	}

	return similarLocationsResult.data ? (
		<>
			{similarLocationsResult.data.closest.length ? (
				<ContentSection>
					<ContentSectionHeading mb={{ base: 5, md: 10 }}>
						{t('Utforska andra kontorshotell i närheten')}
					</ContentSectionHeading>
					<Grid templateColumns="repeat(auto-fill, minmax(250px, 1fr))" gap={5}>
						{similarLocationsResult.data.closest.map((location) => (
							<SimilarLocationCard key={location.id} location={location} />
						))}
					</Grid>
				</ContentSection>
			) : (
				<></>
			)}
			{similarLocationsResult.data.same_operator.length &&
			providerResult.data ? (
				<ContentSection>
					<ContentSectionHeading mb={{ base: 5, md: 10 }}>
						{t('Utforska andra kontorshotell från {{provider_name}}', {
							provider_name: providerResult.data.name,
						})}
					</ContentSectionHeading>
					<Grid templateColumns="repeat(auto-fill, minmax(250px, 1fr))" gap={5}>
						{similarLocationsResult.data.same_operator.map((location) => (
							<SimilarLocationCard key={location.id} location={location} />
						))}
					</Grid>
				</ContentSection>
			) : (
				<></>
			)}
		</>
	) : (
		<></>
	)
}

export default SimilarLocationsSection
